import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { CLIENTS } from "../../constants/testIds";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Building2 } from "lucide-react";

export default function ClientEditor() {
    const { id } = useParams();
    const isNew = id === "new";
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "", contact_name: "", email: "", phone: "", address: "", notes: "",
    });
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (isNew) return;
        api.get(`/clients/${id}`).then((r) => setForm({
            name: r.data.name || "",
            contact_name: r.data.contact_name || "",
            email: r.data.email || "",
            phone: r.data.phone || "",
            address: r.data.address || "",
            notes: r.data.notes || "",
        }));
    }, [id, isNew]);

    const save = async () => {
        try {
            if (isNew) {
                const { data } = await api.post("/clients", form);
                toast.success("Client created");
                nav(`/admin/clients/${data.id}`, { replace: true });
            } else {
                await api.patch(`/clients/${id}`, { ...form, reason: reason || undefined });
                setReason("");
                toast.success("Saved");
            }
        } catch (e) {
            toast.error(apiErr(e));
        }
    };

    return (
        <div data-testid={CLIENTS.editor} className="space-y-8 max-w-3xl">
            <button
                onClick={() => nav("/admin/clients")}
                className="text-muted hover:text-ink flex items-center gap-1.5 text-sm"
            >
                <ArrowLeft size={16} /> Clients
            </button>

            <div className="flex items-center gap-5">
                <div className="w-14 h-14 rounded-md bg-[color:var(--km-forest)]/10 flex items-center justify-center">
                    <Building2 size={22} className="text-forest" strokeWidth={1.5} />
                </div>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted mb-1">
                        {isNew ? "New client" : "Editing"}
                    </div>
                    <h1 className="font-extrabold tracking-[-0.03em] text-3xl leading-none">
                        {form.name || "New client"}
                    </h1>
                </div>
            </div>

            <section className="km-card p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                        <Label>Business name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                        <Label>Contact name</Label>
                        <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                    </div>
                    <div>
                        <Label>Phone</Label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                        <Label>Address</Label>
                        <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Notes</Label>
                        <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                    {!isNew && (
                        <div className="sm:col-span-2">
                            <Label>Reason for change (audit)</Label>
                            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                        </div>
                    )}
                </div>
                <div className="flex justify-end">
                    <Button data-testid={CLIENTS.saveBtn} onClick={save}>
                        {isNew ? "Create client" : "Save changes"}
                    </Button>
                </div>
            </section>
        </div>
    );
}
